import { makeAutoObservable, runInAction } from 'mobx'
import _ from 'lodash'

// evertel
import { decorate, inject, injectable } from '@evertel/di'
import { APIDataDocument } from '@evertel/types'
import { normalize, validate, hasErrors, countErrors } from '@evertel/schema-parser'
import { DepartmentStore } from '@evertel/department'
import { DocumentStore, DocumentSchemaStore } from '../store'
import { Api } from '@evertel/api'
import { MediaUtils } from '../utils'

const defaultTitle = ''

type Platform = 'web' | 'mobile'

class EverdocMediaUplaodError extends Error {
    document: APIDataDocument

    constructor(document, args) {
        super(args)
        this.name = 'EverdocMediaUploadError'
        this.document = document
    }
}

class DocumentCreateController {
    data: Record<string, any> = {}
    departmentId = 0
    documentSchemaId = 0
    name = defaultTitle
    platform: Platform = undefined
    errors?: Record<string, any> = null

    constructor(
        private departmentStore: DepartmentStore,
        private documentStore: DocumentStore,
        private documentSchemaStore: DocumentSchemaStore,
        private api: Api,
        private mediaUtils: MediaUtils
    ) {
        makeAutoObservable(this)
    }

    init = async (departmentId: number, schemaId: number, platform: Platform): Promise<void> => {
        this.departmentId = departmentId
        this.documentSchemaId = schemaId
        this.platform = platform

        if (!this.documentSchema) {
            // if the schema is not in the store, fetch it
            const schema = await this.api.Routes.Department.getDocumentSchemas(this.departmentId, {
                where: {
                    id: schemaId
                }
            })
            runInAction(() => {
                this.documentSchemaStore.update(schema)
            })
        }
    }

    updateData = (data: Record<string, any>) => {
        // called from views to hold the data as they edit the document (state)
        runInAction(() => {
            this.data = data
        })
    }

    create = async (): Promise<APIDataDocument> => {
        let document: APIDataDocument

        // normalize schema
        const schema = normalize(this.documentSchema?.schema)

        // this will throw if fail
        this.validateBeforeUpdate()

        const filteredData = this.normalizeMediaFields(this.data)

        // create an empty document
        document = await this.api.Routes.Department.postDocuments(this.departmentId, {
            documentSchemaId: this.documentSchemaId,
            name: this.name,
            data: filteredData
        })

        runInAction(() => {
            // update the store's data with the new document & schema
            this.documentStore.update(document)
        })

        try {
            // process any media
            const processDocumentMediaData = await this.mediaUtils.processDocumentMedia(
                this.platform,
                this.data,
                document.id as number,
                schema['layouts']['full']
            )

            // update the document with the data
            document = await this.api.Routes.Document.putById(document.id as number, {
                data: processDocumentMediaData
            })

            runInAction(() => {
                // update the store's data with the new document & schema
                this.documentStore.update(document)
            })

            if (this.mediaUtils.errors.length) {
                const messages = this.mediaUtils.errors.map(e => e.message).join(', \n')
                const count = this.mediaUtils.errors.length
                this.mediaUtils.clearErrors()
                throw new EverdocMediaUplaodError(document, `${count} uploads failed: ${messages}`)
            }

        } catch (error: any) {
            throw new EverdocMediaUplaodError(document, error.message)
        }

        //if (notify) this.notify(document.id ?? 0, this.data.isUrgent ?? false)

        this.clearData()

        // return the updated document model
        return this.documentStore.findById(document.id as number) as APIDataDocument
    }

    /**
     * This will find "mediaArrays" shape { uploaded, uploadQueue, deleteQueue } and flatten it to just the uploaded value.
     * Thus uploadQueue items with values like "blob:http://localhost:4200/..." won't cuase WAF 403's on localhost
     * @param obj
     * @returns
     */
    normalizeMediaFields = (value: any): any => {
        const isMediaBundle = (v: any) =>
            _.isObject(v) &&
        !_.isArray(v) &&
        'uploaded' in v && 'uploadQueue' in v && 'deleteQueue' in v

        if (_.isArray(value)) {
            return value.map(this.normalizeMediaFields)
        }

        if (isMediaBundle(value)) {
            return value.uploaded
        }

        if (_.isObject(value)) {
            return _.transform(value, (result, v, k) => {
                result[k] = this.normalizeMediaFields(v)
            }, {} as any)
        }

        return value
    }

    validateBeforeUpdate = () => {
        this.errors = null

        // normalize schema
        const schema = normalize(this.documentSchema?.schema)

        // check for errors in data
        const errors = validate(
            this.data,
            {
                type: 'form',
                items: schema['layouts']['full']
            }
        )

        // if we have any errors, stop and throw error back to view
        if (hasErrors(errors)) {
            runInAction(() => {
                this.errors = errors
            })

            throw new Error(`${this.errorsCount} errors. Please fill out all *required fields before saving.`)
        }

    }

    setName = (name: string) => {
        this.name = name
    }

    notify = (documentId: number, isUrgent: boolean) => {
        this.api.Routes.Document.postNotify(documentId, isUrgent)
    }

    clearData = () => {
        runInAction(() => {
            this.name = ''
            this.data = {}
        })
    }

    get department() {
        return this.departmentStore.findById(this.departmentId)
    }

    get documentSchema() {
        return this.documentSchemaStore.findById(this.documentSchemaId)
    }

    get errorsCount(): number {
        return countErrors(this.errors)
    }

    get defaultTitle() {
        return defaultTitle
    }
}

decorate(injectable(), DocumentCreateController)
decorate(inject(DepartmentStore), DocumentCreateController, 0)
decorate(inject(DocumentStore), DocumentCreateController, 1)
decorate(inject(DocumentSchemaStore), DocumentCreateController, 2)
decorate(inject(Api), DocumentCreateController, 3)
decorate(inject(MediaUtils), DocumentCreateController, 4)

export { DocumentCreateController, EverdocMediaUplaodError }
